import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { View } from 'react-native';
import VacancyStatusDropdown from '../../../components/deac/VacancyStatusDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import VacancyLevelDropdown from '../../../components/deac/VacancyLevelDropdown';
import UserDropdown from '../../../components/deac/UserDropdown';
import VacancyTagDropdown from '../../../components/deac/VacancyTagDropdown';
import { VacancyStatus } from '../../../schemas/deac';

interface Props {
    filters: DeacParamList['Vacancies'];
    setFilters: React.Dispatch<React.SetStateAction<DeacParamList['Vacancies']>>;
    textFilter: string;
    setTextFilter: (value: string) => void;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DeacParamList, 'Vacancies'>>>();
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(filters.actingBodyId ?? 0);
    const [status, setStatus] = useState<VacancyStatus | 'all'>(filters.status ?? 'all');
    const [createdBy, setCreatedBy] = useState<number>(filters.createdBy ?? 0);
    const [levelsIds, setLevelsIds] = useState<number[]>(filters.levelsIds ?? []);
    const [tagsIds, setTagsIds] = useState<number[]>(filters.tagsIds ?? []);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setStatus('all');
        setCreatedBy(0);
        setLevelsIds([]);
        setTagsIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        navigation.setParams({
            ...filters,
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            status: status !== 'all' ? status : undefined,
            createdBy: createdBy !== 0 ? createdBy : undefined,
            levelsIds: levelsIds.length > 0 ? levelsIds : undefined,
            tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            status: status !== 'all' ? status : undefined,
            createdBy: createdBy !== 0 ? createdBy : undefined,
            levelsIds: levelsIds.length > 0 ? levelsIds : undefined,
            tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });
    };

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies) {
            return actingBodyId;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    function getActingBodyUnitId() {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies ||
            authenticatedUser.permissions.deac_manage_acting_body_units_vacancies
        ) {
            return actingBodyUnitId;
        }
        return authenticatedUser.actingBodyUnitId;
    }

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />

            {authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies &&
                <ActingBodiesDropdown
                    includeAllOption={true}
                    value={actingBodyId}
                    setValue={setActingBodyId}
                    zIndex={9}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            {(authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies ||
                authenticatedUser.permissions.deac_manage_acting_body_units_vacancies)
                && <ActingBodiesUnitsDropdown
                    includeAllOption={true}
                    value={actingBodyUnitId}
                    setValue={setActingBodyUnitId}
                    actingBodyId={getActingBodyId()}
                    zIndex={8}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            <ActingBodiesCommandsDropdown
                includeAllOption={true}
                value={actingBodyCommandId}
                setValue={setActingBodyCommandId}
                actingBodyId={getActingBodyId()}
                disabled={false}
                zIndex={7}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <VacancyStatusDropdown
                setValue={setStatus}
                value={status}
                zIndex={6}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <VacancyLevelDropdown
                actingBodyId={getActingBodyId()}
                setValue={setLevelsIds}
                value={levelsIds}
                zIndex={5}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <UserDropdown
                label={translate('createdBy')}
                actingBodyId={getActingBodyId()}
                actingBodyUnitId={getActingBodyUnitId()}
                setValue={setCreatedBy}
                value={createdBy}
                zIndex={4}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <VacancyTagDropdown
                setValue={setTagsIds}
                value={tagsIds}
                zIndex={3}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('beginIn')}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(2)}
                    labelStyle={styles.selectLabelStyle}
                />
            </View>

            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(1)}
                    labelStyle={styles.selectLabelStyle}
                />
            </View>
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));

export default Filter;
